import React, { useState, useEffect } from 'react';

interface LessonPreviewProps {
  onClick?: () => void;
}

type QuestionType = 'bar' | 'pie' | 'pattern' | 'coord' | 'mc';

interface PreviewQuestion {
  type: QuestionType;
  category: string;
  question: string;
}

const PREVIEW_QUESTIONS: PreviewQuestion[] = [
  { type: 'bar', category: 'Data Analysis', question: 'Which state has the most cafes?' },
  { type: 'pie', category: 'Data Analysis', question: 'Which type makes up 45%?' },
  { type: 'pattern', category: 'Patterns', question: 'How many squares in Step 5?' },
  { type: 'coord', category: 'Geometry', question: 'Place a point at (3, 2)' },
  { type: 'mc', category: 'Algebra', question: 'Solve: 2x + 5 = 13' },
];

const MixedLessonPreview: React.FC<LessonPreviewProps> = ({ onClick }) => {
  const [activeQuestion, setActiveQuestion] = useState(0);

  // Cycle through questions
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveQuestion((prev) => (prev + 1) % PREVIEW_QUESTIONS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentQ = PREVIEW_QUESTIONS[activeQuestion];

  // Mini bar chart
  const renderBarChart = () => (
    <svg width="140" height="80" className="mx-auto">
      {[
        { x: 15, h: 25, color: '#f59e0b' },
        { x: 40, h: 40, color: '#8b5cf6' },
        { x: 65, h: 55, color: '#10b981', selected: true },
        { x: 90, h: 35, color: '#3b82f6' },
        { x: 115, h: 20, color: '#06b6d4' },
      ].map((bar, i) => (
        <g key={i}>
          <rect
            x={bar.x}
            y={75 - bar.h}
            width="18"
            height={bar.h}
            rx="2"
            fill={bar.color}
            opacity={bar.selected ? 1 : 0.6}
            stroke={bar.selected ? '#fff' : 'none'}
            strokeWidth={bar.selected ? 2 : 0}
          />
          {bar.selected && (
            <circle cx={bar.x + 9} cy={75 - bar.h - 8} r="6" fill="#10b981">
              <animate attributeName="r" values="6;7;6" dur="1s" repeatCount="indefinite" />
            </circle>
          )}
        </g>
      ))}
      <line x1="10" y1="75" x2="138" y2="75" stroke="#64748b" strokeWidth="1" />
    </svg>
  );

  // Mini pie chart
  const renderPieChart = () => {
    const slices = [
      { start: 0, end: 162, color: '#10b981', selected: true }, // 45%
      { start: 162, end: 252, color: '#8b5cf6' }, // 25%
      { start: 252, end: 324, color: '#3b82f6' }, // 20%
      { start: 324, end: 360, color: '#f59e0b' }, // 10%
    ];
    const cx = 70, cy = 40, r = 35;

    return (
      <svg width="140" height="80" className="mx-auto">
        {slices.map((slice, i) => {
          const startRad = (slice.start - 90) * Math.PI / 180;
          const endRad = (slice.end - 90) * Math.PI / 180;
          const x1 = cx + r * Math.cos(startRad);
          const y1 = cy + r * Math.sin(startRad);
          const x2 = cx + r * Math.cos(endRad);
          const y2 = cy + r * Math.sin(endRad);
          const largeArc = slice.end - slice.start > 180 ? 1 : 0;
          const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;

          return (
            <path
              key={i}
              d={d}
              fill={slice.color}
              opacity={slice.selected ? 1 : 0.6}
              stroke={slice.selected ? '#fff' : '#1e293b'}
              strokeWidth={slice.selected ? 2 : 0.5}
            />
          );
        })}
        <text x="cx" y="40" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">45%</text>
      </svg>
    );
  };

  // Mini pattern
  const renderPattern = () => (
    <div className="flex items-end justify-center gap-3 h-[80px]">
      {[1, 2, 3, 4].map((step) => (
        <div key={step} className="flex flex-col items-center">
          <div className="flex flex-wrap" style={{ width: `${step * 10}px` }}>
            {Array.from({ length: step }).map((_, i) => (
              <div
                key={i}
                className="rounded-sm"
                style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#0ea5e9',
                  margin: '1px',
                }}
              />
            ))}
          </div>
          <span className="text-[7px] mt-1" style={{ color: '#64748b' }}>{step}</span>
        </div>
      ))}
      <div className="flex flex-col items-center">
        <div className="w-6 h-6 rounded border-2 border-dashed border-cyan-400 flex items-center justify-center">
          <span className="text-[8px] font-bold text-cyan-400">?</span>
        </div>
        <span className="text-[7px] mt-1" style={{ color: '#0ea5e9' }}>5</span>
      </div>
    </div>
  );

  // Mini coordinate grid
  const renderCoordGrid = () => {
    const size = 80;
    const cells = 4;
    const cellSize = size / cells;

    return (
      <svg width="140" height="80" className="mx-auto">
        <g transform="translate(30, 0)">
          {/* Grid lines */}
          {Array.from({ length: cells + 1 }).map((_, i) => (
            <g key={i}>
              <line
                x1={i * cellSize}
                y1={0}
                x2={i * cellSize}
                y2={size}
                stroke={i === 2 ? '#64748b' : '#334155'}
                strokeWidth={i === 2 ? 1 : 0.5}
              />
              <line
                x1={0}
                y1={i * cellSize}
                x2={size}
                y2={i * cellSize}
                stroke={i === 2 ? '#64748b' : '#334155'}
                strokeWidth={i === 2 ? 1 : 0.5}
              />
            </g>
          ))}
          {/* Point at (3,2) which is (3,1) in visual coords from center */}
          <circle cx={3 * cellSize} cy={1 * cellSize} r="6" fill="#10b981" stroke="#fff" strokeWidth="1.5">
            <animate attributeName="r" values="6;7;6" dur="1.5s" repeatCount="indefinite" />
          </circle>
          <text x={3 * cellSize + 10} y={1 * cellSize - 5} fontSize="7" fill="#10b981" fontWeight="600">(3,2)</text>
        </g>
      </svg>
    );
  };

  // Mini multiple choice
  const renderMultipleChoice = () => (
    <div className="grid grid-cols-4 gap-1.5 px-3">
      {[3, 4, 5, 6].map((opt) => (
        <div
          key={opt}
          className="p-2 rounded text-center text-xs font-semibold"
          style={{
            backgroundColor: opt === 4 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.05)',
            border: opt === 4 ? '2px solid #10b981' : '1px solid rgba(255, 255, 255, 0.1)',
            color: opt === 4 ? '#10b981' : '#94a3b8',
          }}
        >
          {opt}
        </div>
      ))}
    </div>
  );

  const renderQuestionVisual = () => {
    switch (currentQ.type) {
      case 'bar': return renderBarChart();
      case 'pie': return renderPieChart();
      case 'pattern': return renderPattern();
      case 'coord': return renderCoordGrid();
      case 'mc': return renderMultipleChoice();
      default: return null;
    }
  };

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '200px',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
      >
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ background: '#64748b' }} />
          <div className="h-1.5 w-12 rounded-full" style={{ background: '#10b981' }} />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[8px] font-bold" style={{ color: '#f59e0b' }}>5</span>
          <svg className="w-3 h-3" style={{ color: '#f59e0b' }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 23c-3.866 0-7-3.134-7-7 0-2.551 1.369-4.787 3.455-6.015C8.18 9.793 8 9.418 8 9c0-.552.448-1 1-1 .379 0 .725.214.894.553C10.553 7.612 11.25 6.5 12 5.5c.75 1 1.447 2.112 2.106 3.053.169-.339.515-.553.894-.553.552 0 1 .448 1 1 0 .418-.18.793-.455.985C17.631 11.213 19 13.449 19 16c0 3.866-3.134 7-7 7z"/>
          </svg>
        </div>
      </div>

      {/* Category & Question */}
      <div className="px-3 py-2">
        <span
          className="text-[8px] px-1.5 py-0.5 rounded"
          style={{ background: 'rgba(14, 165, 233, 0.2)', color: '#0ea5e9' }}
        >
          {currentQ.category}
        </span>
        <p className="text-[9px] mt-1.5 font-medium" style={{ color: '#e2e8f0' }}>
          {currentQ.question}
        </p>
      </div>

      {/* Visual */}
      <div className="px-2 pb-2">
        {renderQuestionVisual()}
      </div>

      {/* Question type indicators */}
      <div className="flex justify-center gap-1 pb-2">
        {PREVIEW_QUESTIONS.map((_, i) => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full transition-all"
            style={{
              backgroundColor: i === activeQuestion ? '#0ea5e9' : '#334155',
            }}
          />
        ))}
      </div>

      {/* Footer */}
      <div
        className="px-3 py-2 flex items-center justify-between"
        style={{
          background: 'rgba(16, 185, 129, 0.1)',
          borderTop: '1px solid rgba(16, 185, 129, 0.2)',
        }}
      >
        <div className="flex items-center gap-1">
          <span className="text-[8px]">✓</span>
          <span className="text-[8px] font-semibold" style={{ color: '#10b981' }}>Correct!</span>
          <span className="text-[7px] ml-1" style={{ color: '#10b981' }}>+15 XP</span>
        </div>
        <div
          className="px-2 py-0.5 rounded text-[7px] font-bold"
          style={{ background: '#10b981', color: '#fff' }}
        >
          Continue
        </div>
      </div>

      {/* Hover overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20 pointer-events-none">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.9) 0%, rgba(6, 182, 212, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(14, 165, 233, 0.4)',
          }}
        >
          <svg className="w-4 h-4 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default MixedLessonPreview;
